// localStorage-backed current-viewer identity for the assign-to-reviewer demo.
// Lets a single browser flip between reviewer perspectives without re-auth.
// `"all"` is a special demo value that disables filtering (admin / screenshot view).
import { useCallback, useSyncExternalStore } from "react";
import { REVIEWERS } from "./reviewers";

const STORAGE_KEY = "epocus.demoViewerId";
const SAME_TAB_EVENT = "epocus.demoViewerId.change";

export const ALL_VIEWERS = "all" as const;
export type DemoViewerId = string;

const DEFAULT_VIEWER: DemoViewerId = REVIEWERS[0]?.id ?? ALL_VIEWERS;

const read = (): DemoViewerId => {
  if (typeof window === "undefined") return DEFAULT_VIEWER;
  return window.localStorage.getItem(STORAGE_KEY) ?? DEFAULT_VIEWER;
};

export const setDemoViewer = (id: DemoViewerId): void => {
  window.localStorage.setItem(STORAGE_KEY, id);
  window.dispatchEvent(new CustomEvent(SAME_TAB_EVENT));
};

const subscribe = (listener: () => void): (() => void) => {
  const handler = (e: Event) => {
    if (e instanceof StorageEvent && e.key !== STORAGE_KEY) return;
    listener();
  };
  window.addEventListener("storage", handler);
  window.addEventListener(SAME_TAB_EVENT, handler);
  return () => {
    window.removeEventListener("storage", handler);
    window.removeEventListener(SAME_TAB_EVENT, handler);
  };
};

const getServerSnapshot = (): DemoViewerId => DEFAULT_VIEWER;

export const useDemoViewer = (): DemoViewerId =>
  useSyncExternalStore(subscribe, read, getServerSnapshot);

export const useSetDemoViewer = (): ((id: DemoViewerId) => void) =>
  useCallback(setDemoViewer, []);
